import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  ExecSponsorInboxService,
  WorkstreamExecSponsorInboxReviewItem,
  WorkstreamExecSponsorInboxWorkstream,
  WorkstreamExecSponsorInboxWorkstreamDetail,
  WorkstreamExecSponsorInboxWorkstreamsResponse,
} from 'client';

import {logExecSponsorInboxInteraction} from './execSponsorInboxAnalytics';

export const EXEC_SPONSOR_INBOX_QUERY_KEY = ['workstreams', 'exec-sponsor-inbox'] as const;

const detailQueryKey = (workstreamId: number) =>
  [...EXEC_SPONSOR_INBOX_QUERY_KEY, workstreamId] as const;

// Unread review items plus needs-resolution items: what the list's dot and count show.
export const getInboxPendingCount = (workstream: WorkstreamExecSponsorInboxWorkstream) =>
  (workstream.unread_review_item_count ?? 0) + (workstream.needs_resolution_count ?? 0);

export const useExecSponsorInboxWorkstreams = ({enabled = true}: {enabled?: boolean} = {}) =>
  useQuery({
    queryKey: EXEC_SPONSOR_INBOX_QUERY_KEY,
    queryFn: () =>
      ExecSponsorInboxService.getExecSponsorInboxWorkstreamsApiV1ExecSponsorInboxWorkstreamsGet(),
    enabled,
  });

export const useExecSponsorInboxDetail = (
  workstreamId: number,
  {enabled = true}: {enabled?: boolean} = {}
) =>
  useQuery({
    queryKey: detailQueryKey(workstreamId),
    queryFn: () =>
      ExecSponsorInboxService.getExecSponsorInboxWorkstreamDetailApiV1ExecSponsorInboxWorkstreamsWorkstreamIdGet(
        workstreamId
      ),
    enabled,
  });

export const useMarkReviewItemReviewed = (workstreamId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (item: WorkstreamExecSponsorInboxReviewItem) =>
      ExecSponsorInboxService.markExecSponsorInboxReviewItemReviewedApiV1ExecSponsorInboxWorkstreamsWorkstreamIdReviewItemsItemTypeItemIdPut(
        workstreamId,
        item.item_type,
        item.item_id
      ),
    onSuccess: (review, reviewedItem) => {
      // Update review item on workstream detail page
      queryClient.setQueryData<WorkstreamExecSponsorInboxWorkstreamDetail>(
        detailQueryKey(workstreamId),
        (current) =>
          current
            ? {
                ...current,
                review_items: current.review_items?.map((item) =>
                  item.item_type === reviewedItem.item_type && item.item_id === reviewedItem.item_id
                    ? {...item, ...review, is_reviewed: true}
                    : item
                ),
              }
            : current
      );

      // Update unread review item count in exec sponsor inbox.
      queryClient.setQueryData<WorkstreamExecSponsorInboxWorkstreamsResponse>(
        EXEC_SPONSOR_INBOX_QUERY_KEY,
        (current) =>
          current
            ? {
                ...current,
                workstreams: current.workstreams?.map((item) =>
                  item.id === workstreamId
                    ? {
                        ...item,
                        unread_review_item_count: Math.max(
                          (item.unread_review_item_count ?? 0) - 1,
                          0
                        ),
                      }
                    : item
                ),
              }
            : current
      );

      // Invalidate query since reviewing an item can change server-derived activity timestamp and list order.
      void queryClient.invalidateQueries({
        queryKey: EXEC_SPONSOR_INBOX_QUERY_KEY,
        exact: true,
      });

      logExecSponsorInboxInteraction({
        action: 'marked_reviewed',
        itemType: reviewedItem.item_type,
        outcomeType: reviewedItem.outcome_type,
      });
    },
  });
};
